import Calendar from '@/assets/images/Calendar.svg';
import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import Tick from '@/assets/images/Tick.svg';
import ListHeader from '@/components/common/Header/ListHeader';
import SessionCard from '@/components/common/SessionCard/SessionCard';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { showToast } from '@/components/ui/sonner';
import { GET_ALL_TRAINING_TYPE } from '@/Graphql/queries';
import useCancelSession from '@/hooks/useCancelSession';
import { Session, SessionStatus } from '@/Types/gql/graphql';
import { Button } from '@/components/ui/button';
import { authState } from '@/vars/auth';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { CalendarPlus, Gift, ShoppingBag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ScheduleSession from '../sessions/components/ScheduleSession';
import {
  GET_ALL_SESSIONS,
  GET_ALL_SESSIONS_COUNT,
  GET_SESSION_BALANCE,
} from '../sessions/GraphQl/queries';
import BuySubsctription from '../subscription/components/BuySubsctription';
import RedeemGiftCardModal from '../giftCard/components/RedeemGiftCardModal';
import HowItWorks from './components/HowItWorks';
import ProgramSlider from './components/ProgramSlider';
import TrainerList from './components/TrainerList';

const oneHourAgo = new Date(new Date().getTime() - 60 * 60 * 1000);

export default function Home() {
  const { userDetails } = authState();
  const [open, setOpen] = useState(false);
  const [startingSoon, setStartingSoon] = useState<Session[]>([]);
  const [buySubscriptionOpen, setBuySubscriptionOpen] = useState(false);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [selectedProgramForPurchase, setSelectedProgramForPurchase] =
    useState<string>('fitprime');

  const date = useMemo(() => {
    return dayjs().subtract(1, 'hour');
  }, []);
  const [selectedSession, setSelectedSession] = useState<{ id: string, programId: string, sportId?: string } | null>(null);
  const [sessionData, setSessionData] = useState<Session>();

  const navigate = useNavigate();
  const [cancelDialog, setCancelDialog] = useState(false);
  const [howItWorkOpen, setHowItWorkOpen] = useState(false);
  const { data: sessionBalance, previousData: prevSessionBalance, refetch: refetchBalance } = useQuery(GET_SESSION_BALANCE, { fetchPolicy: 'network-only' });
  // Preserve previous data during refetch so balance values never momentarily drop to 0.
  const effectiveBalance = sessionBalance ?? prevSessionBalance;

  const { data, loading, refetch } = useQuery(GET_ALL_SESSIONS, {
    variables: {
      sort: {
        sessionStartTime: 1,
      },
      filter: {
        traineeId: userDetails?._id,
        sessionStatus: {
          $in: ['Scheduled', 'Open', 'Reassign'],
        },
        sessionStartTime: { $gt: oneHourAgo },
      },
    },
    onCompleted: (data) => {
      // const currentTime = new Date();
      const soonSessions: Session[] = [];
      data?.getAllSession?.forEach((session) => {
        // const startTime = new Date(session?.sessionStartTime);
        const sessionStatus = session?.sessionStatus;

        if (
          sessionStatus === 'Scheduled' ||
          sessionStatus === 'Open' ||
          sessionStatus === SessionStatus.Noshow
        ) {
          soonSessions.push(session as Session);
        }
      });

      // Sort sessions by start time (earliest first)
      soonSessions.sort(
        (a, b) => new Date(a.sessionStartTime).getTime() - new Date(b.sessionStartTime).getTime(),
      );

      // Store only the closest session to the current time
      setStartingSoon(soonSessions.length > 0 ? [soonSessions[0]] : []);
    },
    fetchPolicy: 'network-only',
  });



  const { data: completedCount, loading: completedCountLoading } = useQuery(
    GET_ALL_SESSIONS_COUNT,
    {
      variables: {
        filter: {
          traineeId: userDetails?._id,
          sessionStatus: {
            $in: ['Completed'],
          },
        },
      },
    },
  );

  const { data: scheduledCount, loading: scheduledCountLoading } = useQuery(
    GET_ALL_SESSIONS_COUNT,
    {
      variables: {
        filter: {
          traineeId: userDetails?._id,
          sessionStatus: {
            $in: ['Scheduled', 'Open', 'Reassign'],
          },
          sessionStartTime: { $gt: date },
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  const { data: allSessionCount, loading: allSessionCountLoading } = useQuery(
    GET_ALL_SESSIONS_COUNT,
    {
      variables: {
        filter: {
          traineeId: userDetails?._id,

        },
      },
      fetchPolicy: 'network-only',
    },
  );



  const { data: trainingTypesData } = useQuery(GET_ALL_TRAINING_TYPE);
  const trainingTypes = trainingTypesData?.getAllTrainingType || [];
  const [showAll, setShowAll] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<string | undefined>(undefined);

  // Calculate total balance from all program balances
  const getTotalBalance = () => {
    const fitPrimeBalance = effectiveBalance?.getCurrentUser?.fitPrimeBalance || 0;
    const fitCurveBalance = effectiveBalance?.getCurrentUser?.fitCurveBalance || 0;
    const fitVarsityBalance = effectiveBalance?.getCurrentUser?.fitVarsityBalance || 0;
    const freeSessionBalance = effectiveBalance?.getCurrentUser?.freeSessionBalance || 0;
    return fitPrimeBalance + fitCurveBalance + fitVarsityBalance + freeSessionBalance;
  };

  // Show only first 5 items initially - moved to ProgramSlider component

  const addButtonClick = (program?: string) => {
    const totalBalance = getTotalBalance();

    if (totalBalance > 0) {
      setSessionData(undefined);
      setSelectedProgram(program);
      setOpen(true);
    } else {
      showToast({
        message: 'Session balance is empty. Purchase a package to schedule.',
        variant: 'error',
      });
    }
  };

  const handlePayAsYouGo = (program?: string) => {
    setSelectedProgramForPurchase(program || 'fitprime');
    setBuySubscriptionOpen(true);
  };

  const sessionRefetch = async () => {
    refetch({
      filter: {
        traineeId: userDetails?._id,
      },
      sort: {
        sessionStartTime: -1,
      },
    })
      .then((result) => {
        // If the refetch was successful, update the sessions
        const currentTime = new Date();
        const soonSessions: Session[] = [];
        result?.data?.getAllSession?.forEach((session) => {
          const startTime = new Date(session?.sessionStartTime);
          const sessionStatus = session?.sessionStatus;

          if (
            (sessionStatus === 'Scheduled' ||
              sessionStatus === 'Open' ||
              sessionStatus === SessionStatus.Noshow) &&
            startTime > currentTime
          ) {
            soonSessions.push(session as Session);
          }
        });

        soonSessions.sort(
          (a, b) => new Date(a.sessionStartTime).getTime() - new Date(b.sessionStartTime).getTime(),
        );
        setStartingSoon(soonSessions.length > 0 ? [soonSessions[0]] : []);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Refetch failed:', error);
      });
  };

  // cancellation of session
  const { handleCancelSession } = useCancelSession();

  const handleCancelClick = ({ id, programId, sportId }: { id: string, programId: string, sportId?: string }) => {
    setSelectedSession({ id, programId, sportId });

    setCancelDialog(true);
  };

  // Edit session logic
  const handleEditClick = (session: Session) => {
    setSessionData(session);
    setOpen(true);
  };

  return (
    <>
      <ListHeader
        userName={userDetails?.firstName + ' ' + userDetails?.lastName}
        showSearch={false}
        searchPlaceholder='Search by ID'
        showButton={false}
        filterButton={false}
        onButtonClick={addButtonClick}
        key={userDetails?._id}
        extraActions={
          <div className='hidden sm:flex items-center gap-2'>
            {/* Schedule — outlined, gold text (was gold fill) */}
            <Button
              id='quick-schedule-btn'
              variant='outline'
              className='h-12 px-5 font-bold border-[#F8B107] text-[#F8B107] hover:bg-[#F8B107]/10 hover:text-[#F8B107]'
              onClick={() => addButtonClick()}
            >
              <CalendarPlus className='h-5 w-5 mr-2' />
              Schedule
            </Button>

            {/* Purchase — gold gradient fill */}
            <Button
              id='quick-purchase-btn'
              className='h-12 px-5 font-bold text-black'
              style={{
                background:
                  'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              }}
              onClick={() => setBuySubscriptionOpen(true)}
            >
              <ShoppingBag className='h-5 w-5 mr-2 text-black' />
              Purchase
            </Button>

            {/* Redeem — gold gradient fill */}
            <Button
              id='quick-redeem-btn'
              className='h-12 px-5 font-bold text-black'
              style={{
                background:
                  'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              }}
              onClick={() => setRedeemModalOpen(true)}
            >
              <Gift className='h-5 w-5 mr-2 text-black' />
              Redeem
            </Button>
          </div>
        }
      />

      {/* Mobile quick-action buttons — visible only below sm (640px) */}
      <div className='flex sm:hidden items-center gap-2 px-4 pt-3 pb-1'>
        <Button
          id='mobile-schedule-btn'
          variant='outline'
          className='flex-1 h-10 text-xs font-bold border-[#F8B107] text-[#F8B107] hover:bg-[#F8B107]/10 hover:text-[#F8B107]'
          onClick={() => addButtonClick()}
        >
          <CalendarPlus className='h-4 w-4 mr-1' />
          Schedule
        </Button>
        <Button
          id='mobile-purchase-btn'
          className='flex-1 h-10 text-xs font-bold text-black'
          style={{
            background:
              'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          onClick={() => setBuySubscriptionOpen(true)}
        >
          <ShoppingBag className='h-4 w-4 mr-1 text-black' />
          Purchase
        </Button>
        <Button
          id='mobile-redeem-btn'
          className='flex-1 h-10 text-xs font-bold text-black'
          style={{
            background:
              'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          onClick={() => setRedeemModalOpen(true)}
        >
          <Gift className='h-4 w-4 mr-1 text-black' />
          Redeem
        </Button>
      </div>

      <div className='container mx-auto p-4 space-y-6 py-14 w-[1060px] max-w-[98%]'>
        {/* Stats Section */}
        {allSessionCountLoading ? (
          <div className='space-y-4'>
            {/* Program Cards Container Skeleton */}
            <div
              className='border border-[#515764] rounded-xl p-5 backdrop-blur-sm'
              style={{
                background: 'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)'
              }}
            >
              <Skeleton className='h-6 w-40 mb-4' />
              <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
                {/* FitVarsity Card Skeleton */}
                <Skeleton className='h-32 w-full' />
                {/* FitCurve Card Skeleton */}
                <Skeleton className='h-32 w-full' />
                {/* FitPrime Card Skeleton */}
                <Skeleton className='h-32 w-full' />
              </div>
            </div>
            {/* Session Cards Skeleton */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <Skeleton className='h-32 w-full' />
              <Skeleton className='h-32 w-full' />
            </div>
          </div>
        ) : allSessionCount && allSessionCount?.getAllSessionCount > 0 ? (
          <div className='space-y-4'>
            {/* Program Cards Container */}
            <div
              className='border border-[#515764] rounded-xl p-5 backdrop-blur-sm'
              style={{
                background: 'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)'
              }}
            >
              <h3 className='text-xl font-semibold text-white mb-4'>Sessions Available</h3>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
                {/* FitPrime Card */}
                <Card
                  className='bg-gradient-to-br from-white/10 via-white/17 to-white/23 border border-[#515764] cursor-pointer backdrop-blur-sm'
                  style={{
                    background: 'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)'
                  }}
                  onClick={() => navigate('/subscription')}
                >
                  <CardContent className='flex justify-between items-center p-7'>
                    <div className='flex flex-col gap-3'>
                      <div className='flex flex-col gap-3'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={FitPrimeIcon} alt='FitPrime' className='w-12 h-12' />
                        </div>
                        <h3 className='text-base font-bold text-white tracking-wide'>FitPrime</h3>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className='text-2xl font-bold text-white tracking-wide'>
                        {effectiveBalance?.getCurrentUser?.fitPrimeBalance || 0}
                      </span>
                      <p className='text-xs font-normal text-[#E2E4E8]'>Sessions left</p>
                    </div>
                  </CardContent>
                </Card>


                {/* FitCurve Card */}
                <Card
                  className='bg-gradient-to-br from-white/10 via-white/17 to-white/23 border border-[#515764] cursor-pointer backdrop-blur-sm'
                  style={{
                    background: 'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)'
                  }}
                  onClick={() => navigate('/subscription')}
                >
                  <CardContent className='flex justify-between items-center p-7'>
                    <div className='flex flex-col gap-3'>
                      <div className='flex flex-col gap-3'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={FitCurveIcon} alt='FitCurve' className='w-12 h-12' />
                        </div>
                        <h3 className='text-base font-bold text-white tracking-wide'>FitCurve</h3>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className='text-2xl font-bold text-white tracking-wide'>
                        {effectiveBalance?.getCurrentUser?.fitCurveBalance || 0}
                      </span>
                      <p className='text-xs font-normal text-[#E2E4E8]'>Sessions left</p>
                    </div>
                  </CardContent>
                </Card>
                {/* FitVarsity Card */}
                <Card
                  className='bg-gradient-to-br from-white/10 via-white/17 to-white/23 border border-[#515764] cursor-pointer backdrop-blur-sm'
                  style={{
                    background: 'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)'
                  }}
                  onClick={() => navigate('/subscription')}
                >
                  <CardContent className='flex justify-between items-center p-7'>
                    <div className='flex flex-col gap-3'>
                      <div className='flex flex-col gap-3'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={FitVarsityIcon} alt='FitVarsity' className='w-12 h-12' />
                        </div>
                        <h3 className='text-base font-bold text-white tracking-wide'>FitVarsity</h3>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className='text-2xl font-bold text-white tracking-wide'>
                        {effectiveBalance?.getCurrentUser?.fitVarsityBalance || 0}
                      </span>
                      <p className='text-xs font-normal text-[#E2E4E8]'>Sessions left</p>
                    </div>
                  </CardContent>
                </Card>


              </div>
            </div>

            {/* Session Scheduled and Completed Cards */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <Card
                className='bg-gradient-to-b from-[#F98140] to-[#080923] border-none cursor-pointer'
                onClick={() => navigate('/sessions')}
              >
                {scheduledCountLoading ? (
                  <Skeleton className='bg-bgSecondary min-w-72 h-32' />
                ) : (
                  <CardContent className='flex justify-between items-center p-6'>
                    <div className='flex flex-col gap-4'>
                      <div className='flex flex-col gap-4'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={Calendar} alt='calendar' className='w-8 h-8' />
                        </div>
                        <h3 className='text-base font-semibold text-white'>Session Scheduled</h3>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className='text-2xl font-bold text-white tracking-wide'>
                        {scheduledCount?.getAllSessionCount}
                      </span>
                      <p className='text-xs font-normal text-[#E2E4E8]'>Upcoming</p>
                    </div>
                  </CardContent>
                )}
              </Card>

              <Card
                className='bg-gradient-to-b from-[#2A2D4B] to-[#080923] border-none cursor-pointer'
                onClick={() => navigate('/sessions')}
              >
                {completedCountLoading ? (
                  <Skeleton className='bg-bgSecondary min-w-72 h-32' />
                ) : (
                  <CardContent className='flex justify-between items-center p-6'>
                    <div className='flex flex-col gap-4'>
                      <div className='flex flex-col gap-4'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img src={Tick} alt='tick' className='w-8 h-8' />
                        </div>
                        <h3 className='text-base font-semibold text-white'>Session Completed</h3>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className='text-2xl font-bold text-white tracking-wide'>
                        {completedCount?.getAllSessionCount}
                      </span>
                      <p className='text-xs font-normal text-[#E2E4E8]'>Completed</p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          </div>
        ) : null}

        {/* Schedule Session Section */}
        {startingSoon?.length > 0 ? (
          startingSoon.map((session) => {
            const startTime = new Date(session.sessionStartTime);
            const currentTime = new Date();
            const isSoon = startTime <= new Date(currentTime.getTime() + 60 * 60000);
            const isTrainerMissing = !session.trainerId;
            const status = isSoon && !isTrainerMissing ? '' : 'Scheduled';
            return (
              <div key={`${session?._id}`}>
                <span className='text-base font-semibold mt-5'>Upcoming Session</span>
                <SessionCard
                  key={session._id}
                  session={session}
                  isSoon={isSoon}
                  handleCancelClick={handleCancelClick}
                  handleEditClick={handleEditClick}
                  status={status}
                />
              </div>
            );
          })
        ) : (
          <ProgramSlider
            onScheduleSession={addButtonClick}
            sessionBalance={effectiveBalance?.getCurrentUser ? {
              fitPrimeBalance: effectiveBalance.getCurrentUser.fitPrimeBalance ?? undefined,
              fitCurveBalance: effectiveBalance.getCurrentUser.fitCurveBalance ?? undefined,
              fitVarsityBalance: effectiveBalance.getCurrentUser.fitVarsityBalance ?? undefined,
              freeSessionBalance: effectiveBalance.getCurrentUser.freeSessionBalance ?? undefined,
            } : undefined}
            onHowItWorks={() => setHowItWorkOpen(true)}
            onPayAsYouGo={handlePayAsYouGo}
            loading={loading}
            hasExistingSessions={data && data?.getAllSessionCount > 0}
            trainingTypes={trainingTypes.filter(
              (type): type is NonNullable<typeof type> => type !== null,
            )}
            showAll={showAll}
            onToggleShowAll={() => setShowAll(!showAll)}
          />
        )}
        <div>
          <TrainerList />
        </div>
        {open && (
          <ScheduleSession
            open={open}
            setOpen={setOpen}
            sessionData={sessionData}
            sessionUpdation={sessionRefetch}
            selectedProgram={selectedProgram || 'fitprime'}
            sessionBalance={data && data?.getAllSessionCount}
            freeSessionBalance={effectiveBalance?.getCurrentUser?.freeSessionBalance ?? undefined}
            fitPrimeBalance={effectiveBalance?.getCurrentUser?.fitPrimeBalance ?? undefined}
            fitCurveBalance={effectiveBalance?.getCurrentUser?.fitCurveBalance ?? undefined}
            fitVarsityBalance={effectiveBalance?.getCurrentUser?.fitVarsityBalance ?? undefined}
            ageRange={effectiveBalance?.getCurrentUser?.ageRange ?? undefined}
          />
        )}
        {howItWorkOpen && <HowItWorks open={howItWorkOpen} setOpen={setHowItWorkOpen} />}
        {buySubscriptionOpen && (
          <BuySubsctription
            open={buySubscriptionOpen}
            selectedProgram={selectedProgramForPurchase}
            handleClose={() => setBuySubscriptionOpen(false)}
          />
        )}
        <RedeemGiftCardModal
          open={redeemModalOpen}
          onClose={() => setRedeemModalOpen(false)}
          onSuccess={() => { refetchBalance(); }}
        />

        <CustomDialog
          open={cancelDialog}
          desc='Are you sure you want to cancel session?'
          onCancel={() => setCancelDialog(false)}
          title='Cancel Session'
          onConfirm={() => handleCancelSession(selectedSession?.id || '', selectedSession?.programId || '', selectedSession?.sportId || '')}
        />
      </div>
    </>
  );
}
